"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { fetchWithAuth } from "@/lib/auth";

export function EmailAutoPanel() {
    const [iframeToken, setIframeToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const router = useRouter();

    const fetchToken = useCallback(async (): Promise<string | null> => {
        try {
            const res = await fetchWithAuth("/api/v1/auth/iframe-token", { method: "POST" });
            if (!res.ok) {
                if (res.status === 401 || res.status === 403) {
                    setAuthError(true);
                }
                return null;
            }
            const data = await res.json();
            if (data?.token) {
                setIframeToken(data.token);
                setAuthError(false);
                return data.token;
            }
            return null;
        } catch (err) {
            console.error("Failed to fetch iframe token:", err);
            return null;
        }
    }, []);

    const sendTokenToIframe = useCallback((token: string) => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                { type: "CMTHUB_AUTH_TOKEN", token },
                "*"
            );
        }
    }, []);

    // Initial token fetch on mount
    useEffect(() => {
        let mounted = true;
        fetchToken().then((token) => {
            if (mounted && token) {
                setIframeToken(token);
            }
        });
        return () => {
            mounted = false;
        };
    }, [fetchToken]);

    // Listen for postMessage from Email Auto iframe
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data?.type === "CMTHUB_SESSION_EXPIRED") {
                router.push("/login?next=/staff");
            } else if (event.data?.type === "CMTHUB_REQUEST_TOKEN") {
                const freshToken = await fetchToken();
                if (freshToken) {
                    sendTokenToIframe(freshToken);
                } else {
                    router.push("/login?next=/staff");
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [fetchToken, sendTokenToIframe, router]);

    // Proactively refresh the iframe token every 10 minutes (token lives for 15 minutes)
    useEffect(() => {
        const intervalId = setInterval(async () => {
            const freshToken = await fetchToken();
            if (freshToken) {
                sendTokenToIframe(freshToken);
            }
        }, 10 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [fetchToken, sendTokenToIframe]);

    const handleIframeLoad = () => {
        setIsLoading(false);
        if (iframeToken) {
            sendTokenToIframe(iframeToken);
        }
    };

    if (authError) {
        return (
            <div className="flex flex-col items-center justify-center flex-1 p-8 text-center bg-[#f0f5f6]">
                <div className="max-w-md p-6 bg-white rounded-xl shadow-sm border border-red-100">
                    <p className="text-red-700 font-semibold mb-2">Staff Authentication Required</p>
                    <p className="text-sm text-slate-600 mb-4">
                        Your session does not have access to the Email Automation suite. Please re-authenticate with a staff account.
                    </p>
                    <button
                        onClick={() => router.push("/login?next=/staff")}
                        className="px-4 py-2 bg-[#007a8a] text-white text-xs font-semibold rounded-lg hover:bg-[#00626f] transition"
                    >
                        Sign In as Staff
                    </button>
                </div>
            </div>
        );
    }

    // Pass the token as query parameter to ensure immediate bootstrap on page load
    const emailAutoUrl = iframeToken
        ? `/email_auto_ui/index.html?token=${encodeURIComponent(iframeToken)}`
        : "/email_auto_ui/index.html";

    return (
        <div className="relative w-full h-full flex flex-col flex-1 bg-[#f0f5f6] overflow-hidden">
            {isLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 z-50 transition-all duration-300">
                    <div className="w-12 h-12 border-4 border-[#003d45]/15 border-t-[#007a8a] rounded-full animate-spin mb-4" />
                    <p className="text-[#003d45] font-bold text-[11px] tracking-widest uppercase">Loading Email Automation...</p>
                </div>
            )}
            {iframeToken && (
                <iframe
                    ref={iframeRef}
                    src={emailAutoUrl}
                    className="w-full h-full border-none flex-1"
                    onLoad={handleIframeLoad}
                    title="Email Automation"
                />
            )}
        </div>
    );
}
